import React, { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import EditButton from "../../../../../components/EditButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import { toast } from "../../../../../components/toastNotification";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";
import ModelForm from "../form/ModelForm";
import Timer from "components/Timer";
import useGetModelList from "../hooks/useGetModel";
import useDeleteModel from "../hooks/useDeleteModel";
import {
  addStateModel,
  editStateModel,
  modelFetching,
  modelFilterText,
  rowEditModel,
  selectedRowsStateModel,
} from "../atoms/atoms";
import { IModel } from "../types";
import TableFilters from "components/TableFilters";

export const ModelTable: React.FC = ({}) => {
  const { data: directData, refetch: reFetch } = useGetModelList();

  const BulkDelete = useDeleteModel();

  //Atoms Call
  const [actionAddState, setActionAddState] = useRecoilState(addStateModel);
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateModel
  );
  const [actionEditState, setActionEditState] = useRecoilState(editStateModel);
  const [filterTextState, setFilterTextState] = useRecoilState(modelFilterText);
  const setEditRowData = useSetRecoilState(rowEditModel);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(modelFetching);

  //Local States
  const [tableData, setTableData] = useState<IModel[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<IModel[]>([]);
  const [displayNote, setDisplayNote] = useState(false);
  const [edit, setEdit] = useState(false);

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_leasedequipment_model_add;
  const deletePermission =
    PERMISSIONS.permission_utilities_leasedequipment_model_delete;
  const editPermission =
    PERMISSIONS.permission_utilities_leasedequipment_model_edit;

  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };
  // Get Data
  useEffect(() => {
    if (directData?.status == 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  // For Recent Activity + Search
  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      ).then((result: IModel[] | undefined) => {
        if (typeof result !== "object") {
          toast.fire("Failed", result, "error");
        } else {
          setTableFilteredData(result);
          setRecent(result[0].modelName);
        }
      });
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
    setSelectedRows([]);
  }, [recordIdFromRecent]);

  //For Filter/Search
  const filtereditems =
    tableData &&
    tableData?.filter(
      (item) =>
        item.modelName &&
        item.modelName
          .toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
    );
  useEffect(() => {
    if (!recent) {
      setTableFilteredData(filtereditems);
    }
  }, [filterTextState]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = async () => {
    setIsFetching(true);
    const ids = selectedRows && selectedRows.map((row) => row.modelId);
    BulkDelete(ids);
    setSelectedRows([]);
    setOpenModalDelete(false);
    await Timer();
    reFetch();
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Models" : "Model"
    }?`}</div>
  );

  const matColumns = [
    {
      title: "Model Name",
      field: "modelName",
      customSort: (a, b) =>
        a.modelName && b.modelName
          ? a.modelName.trim().localeCompare(b.modelName.trim())
          : a.modelName
          ? 0
          : -1,
    },
    {
      title: "Description",
      field: "description",
      customSort: (a, b) =>
        a.description && b.description
          ? a.description.trim().localeCompare(b.description.trim())
          : a.description
          ? 0
          : -1,
    },
    {
      title: "Make Name",
      field: "makeName",
      customSort: (a, b) =>
        a.makeName && b.makeName
          ? a.makeName.trim().localeCompare(b.makeName.trim())
          : a.makeName
          ? 0
          : -1,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];
  return (
    <div className="bg-white p-4">
      {actionAddState || actionEditState ? (
        <div>
          <ModelForm reFetch={reFetch} />
        </div>
      ) : (
        <>
          <div className="flex justify-between text-xs items-center w-full">
            <TableFilters
              defaultText={filterTextState}
              recentText={recent}
              setRecent={setRecent}
              textInputName={"ModelName"}
              columnDetails={"Model Name"}
              filterText={setFilterTextState}
              disabledButton={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              disabledTextBox={
                selectedRows !== undefined && selectedRows.length > 0
              }
            />

            <div className="">
              {userPermissions.includes(addPermission) ? (
                <AddButton
                  buttonText="New Model"
                  onClick={() => handleAdd()}
                  disabled={
                    actionEditState ||
                    (selectedRows !== undefined && selectedRows.length > 0)
                  }
                />
              ) : (
                ""
              )}
            </div>
          </div>

          <div>
            {userPermissions.includes(deletePermission) ? (
              <div className="text-secondary italic text-xs3 my-2">
                <p className="font-bold italic inline-block mr-1">Note:</p>
                <span>Tick the box to deactivate some data.</span>
              </div>
            ) : (
              <div className="mb-4"></div>
            )}
          </div>

          <div className="">
            <MaterialTableTS
              data={
                isFetching
                  ? []
                  : filterTextState === "" && recent === ""
                  ? tableData
                  : filterTextState === ""
                  ? tableFilteredData
                  : filtereditems
              }
              showTitle={true}
              columns={matColumns}
              isLoading={isFetching}
              onSelectionChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              selection={
                !actionEditState &&
                !actionAddState &&
                userPermissions.includes(deletePermission)
              }
              setConfirmModalOpen={setOpenModalDelete}
            />
          </div>

          <ConfirmBoxDelete
            ModalTitle="Deactivate Model"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => {
              setOpenModalDelete(false);
            }}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </>
      )}
    </div>
  );
};
export default ModelTable;
